import { useEffect, useState } from 'react';

import type { ContarInput, LugarFiltro } from '@/api/calendarioForense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useContarPrazo, useDiasQueNaoContam } from '@/hooks/useCalendarioForense';
import { classifyError, getUserVisibleErrorMessage } from '@/lib/errors';
import { fmtData, hojeIso, somarDias } from './calendarioForense';

const DIA_DA_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

function diaDaSemana(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  return DIA_DA_SEMANA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
}

function DiasQueNaoContam({ lugar }: { lugar: LugarFiltro }) {
  const [de, setDe] = useState(() => hojeIso());
  const [ate, setAte] = useState(() => somarDias(hojeIso(), 180));
  const valido = Boolean(de && ate && ate >= de);
  const { data, isLoading, error } = useDiasQueNaoContam(lugar, de, ate, valido);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">O que não conta neste lugar</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="space-y-1">
            <Label htmlFor="conf-de">De</Label>
            <Input id="conf-de" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="conf-ate">Até</Label>
            <Input id="conf-ate" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
          </div>
        </div>
        {isLoading && <Spinner size={32} label="Carregando os dias" />}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(error).message}</AlertDescription>
          </Alert>
        )}
        {data && data.length === 0 && (
          <p className="text-sm text-muted-foreground">Todos os dias de semana do período contam.</p>
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-border/60">
            {data.map((d) => (
              <li key={d.data} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2 text-sm">
                <span className="w-28 shrink-0 tabular-nums">
                  {diaDaSemana(d.data)}, {fmtData(d.data)}
                </span>
                <Badge variant={d.efeito === 'feriado' ? 'secondary' : 'outline'}>
                  {d.efeito === 'feriado' ? 'Sem expediente' : 'Prazo suspenso'}
                </Badge>
                <span className="text-muted-foreground">{d.motivos.join('; ')}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ContarUmPrazo({ lugar }: { lugar: LugarFiltro }) {
  const contar = useContarPrazo();
  const [form, setForm] = useState<ContarInput>(() => ({
    data: hojeIso(),
    dias: 15,
    forma: 'djen',
    regime: 'uteis',
    em_dobro: false,
  }));
  const { reset } = contar;
  // O resultado é do lugar e dos dados com que foi contado: mudou qualquer um, ele sai.
  useEffect(() => reset(), [lugar, reset]);
  const set = <K extends keyof ContarInput>(campo: K, valor: ContarInput[K]) => {
    reset();
    setForm((f) => ({ ...f, [campo]: valor }));
  };
  const diasValidos = form.dias >= 1 && form.dias <= 365;
  const r = contar.data;
  // Fora do calendário ou dado recusado (422) é resposta da conta, não falha.
  const erro = contar.error
    ? classifyError(contar.error).status === 422
      ? getUserVisibleErrorMessage(contar.error)
      : classifyError(contar.error).message
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Contar um prazo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.data && diasValidos) contar.mutate({ lugar, input: form });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="contar-data">{form.forma === 'djen' ? 'Disponibilizado em' : 'Publicado em'}</Label>
            <Input id="contar-data" type="date" value={form.data} onChange={(e) => set('data', e.target.value)} />
          </div>
          <div className="w-24 space-y-1">
            <Label htmlFor="contar-dias">Dias</Label>
            <Input
              id="contar-dias"
              inputMode="numeric"
              value={String(form.dias)}
              aria-invalid={!diasValidos}
              aria-describedby="contar-dias-erro"
              onChange={(e) => set('dias', Number(e.target.value.replace(/\D/g, '').slice(0, 3)) || 0)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="contar-forma">Comunicação</Label>
            <Select value={form.forma} onValueChange={(v) => set('forma', v as ContarInput['forma'])}>
              <SelectTrigger id="contar-forma" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="djen">DJEN</SelectItem>
                <SelectItem value="dje">DJe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="contar-regime">Contagem</Label>
            <Select value={form.regime} onValueChange={(v) => set('regime', v as ContarInput['regime'])}>
              <SelectTrigger id="contar-regime" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="uteis">Dias úteis</SelectItem>
                <SelectItem value="corridos">Dias corridos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-h-11 items-center gap-2">
            <Switch id="contar-dobro" checked={form.em_dobro} onCheckedChange={(v) => set('em_dobro', v)} />
            <Label htmlFor="contar-dobro">Em dobro</Label>
          </div>
          <Button type="submit" className="min-h-11" disabled={contar.isPending}>
            {contar.isPending && <Spinner className="mr-2 text-inherit" />}
            Contar
          </Button>
        </form>

        {!diasValidos && (
          <p id="contar-dias-erro" className="text-sm text-destructive">
            O prazo deve ter de 1 a 365 dias.
          </p>
        )}

        {erro && (
          <Alert variant="destructive">
            <AlertDescription>{erro}</AlertDescription>
          </Alert>
        )}

        {r && !contar.isPending && (
          <div className="space-y-2" aria-live="polite">
            <p className="text-lg font-semibold">Vence em {fmtData(r.vencimento)}</p>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              {r.passos.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** A conferência no lugar escolhido no filtro: o calendário como está no banco agora. */
export function ConferirCalendario({ lugar }: { lugar: LugarFiltro }) {
  return (
    <div className="space-y-4">
      {!lugar.justica && (
        <Alert>
          <AlertDescription>
            Sem a justiça no lugar, as regras de uma justiça só (o Carnaval e a Semana Santa da Justiça Federal e da do
            Trabalho, por exemplo) ficam fora da conta. Escolha a justiça para conferir como o JurisApp conta.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <ContarUmPrazo lugar={lugar} />
        <DiasQueNaoContam lugar={lugar} />
      </div>
    </div>
  );
}
